import { GoogleAnalyticsService } from '@rightship-tech/common-ui/services/analytics/google-analytics.service';
import { autoinject, transient, useView } from "aurelia-framework";
import { PowerBiService } from "../../../../../../services/power-bi/power-bi.service";
import { ReportsService } from "../../../../../../services/reports/reports.service";
import { EmbedFilterTypeEnum, EmbedParams } from "../../../../../../models/power-bi/power-bi.models";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { Router } from "aurelia-router";
import { Claims } from "../../../../../../constants/authentication/claims";
import { UserClaimsService } from "../../../../../../services/authentication/user-claims.service";
import { ConfigService } from '@rightship-tech/common-ui/services/basic/config.service';
import { type IReportEmbedConfiguration, type Embed, type Report } from 'powerbi-client';
//This import populates the powerbi object in the window
await import('powerbi-client');

@autoinject
@transient()
@useView('views/pages/modules/rightship-insights/powerbi/powerbi-report/powerbi.html')
export class PowerBi {

    reportId: number;
    entityTypeId: number;
    entityId: number;
    oldEntityId: number;
    title: string;
    defaultTitle: string;
    isLoading: boolean;
    showResult: boolean;
    filterByCustomer: boolean;
    reportElement: HTMLDivElement;
    report: Embed | Report;
    embedParams: EmbedParams;
    timeStart: number;
    isRightShipUser: boolean;
    showRequestRightshipInspectionButton: boolean;

    constructor(private powerBiService: PowerBiService,
        private reportsService: ReportsService,
        private router: Router,
        userClaimService: UserClaimsService,
        private googleAnalyticsService: GoogleAnalyticsService, protected configService: ConfigService) {
        this.isRightShipUser = userClaimService.hasPermission(Claims.RightShipUser);
    }

    async activate(model: {
        id: number;
        guid: string;
        entityTypeId: number;
        entityId: number;
        title: string;
    }) {
        if (!model.id) {
            this.reportId = await this.reportsService.getReportIdByGuid(model.guid);
        } else {
            this.reportId = model.id;
        }
        this.entityTypeId = model.entityTypeId;
        this.entityId = model.entityId;
        this.title = model.title;
        this.defaultTitle = model.title;
        this.oldEntityId = model.entityId;
        this.filterByCustomer = model.entityTypeId == EntityTypes.Customer;

        //Check if report is 'Global RightShip Inspections'. If yes, show the button - DEL-11010
        if (model.title == 'Global RightShip Inspections') {
            this.showRequestRightshipInspectionButton = true;
        }
    }

    async applyCustomerFilter() {
        if (this.entityId != this.oldEntityId) {
            this.oldEntityId = this.entityId;
            await this.attached();
        }
    }

    attached() {
        this.timeStart = Date.now();

        this.reportElement['powerBiEmbed'] = null; //reset the embed to prevent issue
        if (!this.entityId && this.filterByCustomer) {
            this.showResult = false;
            this.title = this.defaultTitle;
            return;
        }
        this.showResult = true;
        this.isLoading = true;

        console.time('-----------getFullReportName & getEmbedInfo');
        const getCustomReportNamePromise = this.reportsService.getFullReportName(this.reportId, this.entityId, this.entityTypeId);
        const getEmbedInfoPromise = this.powerBiService.getEmbedInfo(this.reportId, this.entityTypeId, this.entityId);
        Promise.all([getCustomReportNamePromise, getEmbedInfoPromise]).then(results => {
            console.timeEnd('-----------getFullReportName & getEmbedInfo');

            this.title = results[0];
            this.embedParams = results[1];

            const config: IReportEmbedConfiguration = {
                type: 'report',
                tokenType: 1,
                accessToken: this.embedParams.embedToken.token,
                embedUrl: this.embedParams.embedReports[0].embedUrl,
                settings: {
                    navContentPaneEnabled: this.embedParams.navContentEnabled,
                    panes: {
                        filters: {
                            visible: false
                        },
                        pageNavigation: {
                            visible: false
                        }
                    }
                }
            };

            const powerbi = window["powerbi"];
            this.report = powerbi.embed(this.reportElement, config);

            this.report.iframe.addEventListener('load', this.onReportLoaded);

            console.time('-----------embedding ui');
        });

    }

    detached() {
        const timeStop = Date.now();

        this.googleAnalyticsService.sendCustomGA4EventMulti("power_bi_dashboard", {
            "title": this.title,
            "time_start": new Date(this.timeStart),
            "time_spent": (timeStop - this.timeStart) / 1000 //converts to seconds
        })
    }

    onReportLoaded: () => Promise<void> = async () => {

        console.timeEnd('-----------embedding ui');
        this.isLoading = false;
        if ('iframe' in this.report) {
            this.report.iframe.removeEventListener('loaded', this.onReportLoaded);
        }

        try {
            const pages = 'getPages' in this.report ? await this.report.getPages() : [];

            if (this.embedParams.filters && this.embedParams.filters.length > 0) {
                for (const filter of this.embedParams.filters) {
                    if (filter.filterType == EmbedFilterTypeEnum.Filter) {
                        await this.setFilter([filter.value], filter.tableName, filter.columnName, filter.operator, pages);
                    }
                    else if (filter.filterType == EmbedFilterTypeEnum.Slicer) {
                        await this.setSlicerFilter([filter.value], filter.tableName, filter.columnName, filter.operator, pages);
                    }
                }
            }
        } catch (e) {
            console.error("Failed to load powerbi report", e);
        }
    };

    async setFilter(selectedFilterValues, tableName, columnName, operator, pages) {
        for (const page of pages) {
            const pageFilters = [];
            const filters = await page.getFilters();
            filters.forEach(x => {
                if (x.target.table === tableName && x.target.column === columnName) {
                    x.operator = operator;
                    x.values = selectedFilterValues;
                }
                pageFilters.push(x);
            });
            await page.setFilters(pageFilters);
        }
    }

    async setSlicerFilter(selectedFilterValues, tableName, columnName, operator, pages) {
        for (const page of pages.filter(x => x.isActive)) {
            const visuals = await page.getVisuals();
            const slicers = visuals.filter(v => v.type === "slicer");

            const filter = {
                $schema: 'http://powerbi.com/product/schema#basic',
                target: {
                    table: tableName,
                    column: columnName
                },
                operator: operator,
                values: selectedFilterValues,
            };

            for (const visual of slicers) {
                const state = await visual.getSlicerState();
                if (state && state.targets && state.targets.filter(target => target.table === tableName && target.column === columnName).length > 0) {
                    await visual.setSlicerState({
                        filters: [filter]
                    })
                }
            }
        }
    }

    public navigateBack() {
        this.router.navigateBack();
    }

    navigateToRequestRsInsp() {
        if (this.configService.config.settings.redirectInspectionLinkToMicrosite)
            window.open(this.configService.config.urls.rightShipSignupUrl + "/?utm_campaign=rs-insp-request", "_blank");
        else
            window.open(this.configService.config.urls.rightShipPlatformUrl + "/inspection-client/inspection/1", "_blank");
    }
}